import { useTranslation } from 'react-i18next';

import type { Card } from '@/entities/board';

import { MAX_CHECKLIST_INDICATORS, unfinishedChecklists } from './checklists';
import styles from './Kanban.module.css';

/** The circle's ring: a one-color stroke (the pin color) that CSS "draws" when it appears. */
export function CheckRing() {
  return (
    <svg className={styles.ring} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.5" pathLength="100" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M6 2h4l-.5 4 2 2.5H4.5L6.5 6zM8 8.5V14"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M2 3h12v3H2zM3 6v7h10V6M6.5 9h3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="m13 7.5-4.8 4.8a2.8 2.8 0 0 1-4-4L9 3.5a1.9 1.9 0 0 1 2.7 2.7L7 11a1 1 0 0 1-1.4-1.4l4.3-4.3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M13 8a5 5 0 1 1-1.6-3.7M13 2.5v3h-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface Props {
  card: Card;
  completed: boolean;
  pinPending: boolean;
  onUnpin: (cardId: string) => void;
  onArchive: (cardId: string) => void;
  /** The tile is a link preview: offer "refresh preview" in a group of its own. */
  linkPreview?: boolean;
  refreshing?: boolean;
  onRefreshPreview?: () => void;
}

/**
 * Vertical action rail on the right of a card tile. Fixed order, top to bottom:
 * 1. Pin (only when pinned; clicking it unpins),
 * 2. Archive (only for a completed card),
 * 3. one indicator per unfinished checklist (max three, then `…`),
 * 3b. a paperclip with the number of attached files (none: hidden; links are not files),
 * 4. for link previews, "refresh preview" in a SEPARATE group below with a clear gap, so it is
 *    never mistaken for (or hit instead of) the pin / archive actions.
 */
export function ActionRail({
  card,
  completed,
  pinPending,
  onUnpin,
  onArchive,
  linkPreview = false,
  refreshing = false,
  onRefreshPreview,
}: Props) {
  const { t } = useTranslation();
  const pinned = card.placement.is_pinned;
  const checklists = unfinishedChecklists(card);
  const shown = checklists.slice(0, MAX_CHECKLIST_INDICATORS);
  const hidden = checklists.length - shown.length;
  const files = card.attachment_count;
  if (!pinned && !completed && checklists.length === 0 && files === 0 && !linkPreview) return null;
  return (
    <div className={styles.rail} data-testid="card-rail">
      {pinned && (
        <button
          type="button"
          className={`${styles.railButton} ${styles.pinned}`}
          data-rail="pin"
          title={t('cards.unpinHint')}
          aria-label={t('cards.unpin')}
          disabled={pinPending}
          onClick={() => {
            onUnpin(card.id);
          }}
        >
          <PinIcon />
        </button>
      )}
      {completed && (
        <button
          type="button"
          className={styles.railButton}
          data-rail="archive"
          title={t('cards.archiveHint')}
          aria-label={t('cards.archive')}
          onClick={() => {
            onArchive(card.id);
          }}
        >
          <ArchiveIcon />
        </button>
      )}
      {shown.map((checklist) => (
        <span
          key={checklist.id}
          className={styles.railIndicator}
          data-rail="checklist"
          title={checklist.title ?? undefined}
          role="img"
          aria-label={t('checklist.indicator', {
            title: checklist.title || t('checklist.untitled'),
            completed: checklist.completed,
            total: checklist.total,
          })}
        >
          ☑ {checklist.completed}/{checklist.total}
        </span>
      ))}
      {hidden > 0 && (
        <span
          className={`${styles.railIndicator} ${styles.railMore}`}
          data-rail="more"
          role="img"
          aria-label={t('checklist.moreUnfinished', { count: hidden })}
          title={t('checklist.moreUnfinished', { count: hidden })}
        >
          …
        </span>
      )}
      {files > 0 && (
        <span
          className={styles.railIndicator}
          data-rail="attachments"
          role="img"
          aria-label={t('attachments.rail', { count: files })}
          title={t('attachments.rail', { count: files })}
        >
          <PaperclipIcon /> {files}
        </span>
      )}
      {linkPreview && (
        <div className={styles.railGroup} data-rail-group="preview">
          <button
            type="button"
            className={`${styles.railButton} ${refreshing ? styles.refreshing : ''}`}
            data-rail="refresh"
            title={t('cards.refreshPreview')}
            aria-label={t('cards.refreshPreview')}
            disabled={refreshing}
            onClick={onRefreshPreview}
          >
            <RefreshIcon />
          </button>
        </div>
      )}
    </div>
  );
}
